import Http from 'common/services/Http';
import TrackingDevice from './models/TrackingDevice';
import TripDriverHistoryItem from './models/TripDriverHistoryItem';
import TrackingRoutes from './models/TrackingRoutes';
import { TrackingItemDto } from './models/TrackingItemDto';
import TrackingRoutesDays from './models/TrackingRoutesDays';
import TrackingVehicle from './models/TrackingVehicle';
import TrackingVehicleSearchCriteria from './models/TrackingVehicleSearchCriteria';
import { VehiclesSearchCriteria } from 'api/vehicles/models/VehiclesSearchCriteria';
import { Paged } from 'api/common/types/Page';
import { FailedGpsProvider } from './models/FailedGpsProvider';
import TrackingVehicleDevice from './models/TrackingVehicleDevice';
import { TrackingRouteSearchCriteria } from './models/TrackingRouteSearchCriteria';
import { TrackingRouteGroupSearchCriteria } from './models/TrackingRouteGroupSearchCriteria';
import { TrackingRoutesGroup } from './models/TrackingRoutesGroup';
import { AddressHydrationStatus } from './models/AddressHydrationStatus';
import moment from 'moment';

export type PrivacyStatus = {
    isPrivate: boolean;
    isPartial?: boolean;
    type?: string;
    activatedByName?: string | null;
    activatedAt?: string | null;
    resumesAt?: string | null;
};


class TrackingService {

    public getVehiclesByLocator(id: string, excludeVehicleId?: string) {
        return Http.get<TrackingVehicle[]>(`tracking/gps-all-vehicles-for-locator/${id}`, excludeVehicleId ? { excludeVehicleId } : undefined);
    }

    public getGpsVehicles() {
        return Http.get<TrackingVehicle[]>(`tracking/gps-all-vehicles`);
    }

    // Task #66078: push the vehicle capacity (tank liters / battery kWh) to GPSTracking after save.
    public pushVehicleCapacity(vehicleId: string, tankCapacity?: number | null, batteryCapacityKwh?: number | null) {
        return Http.post('tracking/push-vehicle-capacity', { vehicleId, tankCapacity, batteryCapacityKwh });
    }

    public getGpsVehiclesPosition(vehicleIds: string[]) {
        return Http.post<TrackingItemDto[]>(`tracking/gps-vehicles-position`, { vehicleIds });
    }
    public getTripDays(vehicleId: string) {
        return Http.post<TrackingRoutesDays[]>('tracking/gps-vehicle-trips-days', { vehicleId });
    }

    public getGpsTrips(criteria: TrackingRouteSearchCriteria) {
        return Http.get<TrackingRoutes[]>('tracking/gps-vehicle-trips', criteria);
    }

    public getGpsWaypoints(id: string) {
        return Http.get<TrackingItemDto[]>(`tracking/gps-waypoints/${id}`);
    }

    public updateGpsAddress(id: string, address: string, isLast: boolean) {
        return Http.put<string>('tracking/gps-trips-address/' + id, { id, address, isLast });
    }

    public getGpsVehiclesNearest(criteria: TrackingVehicleSearchCriteria) {
        return Http.get<Paged<TrackingItemDto> & { failedProviders?: FailedGpsProvider[] }>('tracking/gps-vehicles-nearest', criteria);
    }

    public hydrateGpsData(criteria: TrackingRouteGroupSearchCriteria) {
        return Http.get<TrackingRoutesGroup[]>('tracking/gps-hydrate-data', criteria);
    }


    public getVehicleDevices(criteria: VehiclesSearchCriteria) {
        return Http.get<Paged<TrackingVehicleDevice>>('tracking/gps-vehicles-devices', criteria);
    }

    public getVehicleDevice(id: string) {
        return Http.get<TrackingVehicleDevice>('tracking/gps-vehicle-device/' + id);
    }

    public getDeviceImeisList() {
        return Http.get<TrackingDevice[]>(`tracking/device-imeis`);
    }

    public changeVehicleDevice(vehicleId: string, deviceId: string, keepHistory: boolean, km?: number) {
        return Http.post('tracking/change-vehicle-device', { vehicleId, deviceId, km, keepHistory });
    }

    public getGpsVehiclesForVehicle(id?: string) {
        return Http.get<TrackingDevice[]>('tracking/gps-all-vehicles-for-vehicle/' + id);
    }

    public startExportGpsRoutes(vehicleId: string, startDate: Date | null, endDate: Date | null, exportType: string) {
        return Http.post<{ jobId: string }>('tracking/export-gps-routes-start', {
            vehicleId,
            exportType,
            startDate: startDate ? moment(startDate).format('YYYY-MM-DD') : undefined,
            endDate: endDate ? moment(endDate).format('YYYY-MM-DD') : undefined
        });
    }

    public downloadExportFile(jobId: string) {
        return Http.getWithConfig<Blob>(`tracking/export-gps-routes-download/${jobId}`, null, { responseType: 'blob' });
    }

    public getExportProgress(jobId: string) {
        return Http.get<{ progress: number; status: string; statusMessage?: string; totalTrips?: number; processedTrips?: number; message?: string }>(`tracking/export-gps-routes-progress/${jobId}`);
    }

    public getTripDriverHistory(tripId: string) {
        return Http.get<TripDriverHistoryItem[]>(`tracking/trip-driver-history/${tripId}`);
    }

    public getPrivacyStatus(vehicleId: string, date: string) {
        return Http.get<PrivacyStatus>(`tracking/privacy-status`, { vehicleId, date });
    }

    public getPrivacyStatusBatch(vehicleIds: string[], date: string) {
        return Http.post<Record<string, PrivacyStatus>>(`tracking/privacy-status-batch`, { vehicleIds, date });
    }

    // God mode (Platform Admin): forçar a hidratação das moradas em falta nas viagens.
    public runAddressHydration() {
        return Http.post('tracking/god-mode/run-address-hydration', {});
    }

    public getAddressHydrationStatus() {
        return Http.get<AddressHydrationStatus>('tracking/god-mode/run-address-hydration/status');
    }

}

export default new TrackingService();